import React, { useEffect, useState } from 'react';
import { ArrowRight, Loader2, Plug } from 'lucide-react';

interface DesktopServerSetupPageProps {
  organizationName?: string;
  initialError?: string | null;
}

export const DesktopServerSetupPage: React.FC<DesktopServerSetupPageProps> = ({ organizationName, initialError }) => {
  const [primaryServerUrl, setPrimaryServerUrl] = useState('');
  const [reserveServerUrl, setReserveServerUrl] = useState('');
  const [activeServerKind, setActiveServerKind] = useState<'primary' | 'reserve' | 'unknown'>('unknown');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resolvedOrgName = String(organizationName || 'АрхивЛинк');
  const orgInitial = (resolvedOrgName.trim()[0] || 'А').toUpperCase();

  useEffect(() => {
    setError(String(initialError || ''));
  }, [initialError]);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await window.desktopApi?.getConfig();
        if (cfg?.primaryServerUrl || cfg?.serverUrl) setPrimaryServerUrl(String(cfg.primaryServerUrl || cfg.serverUrl || ''));
        if (cfg?.reserveServerUrl) setReserveServerUrl(String(cfg.reserveServerUrl));
        if (cfg?.activeServerKind) setActiveServerKind(cfg.activeServerKind);
      } catch {}
    })();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const primaryRaw = String(fd.get('primaryServerUrl') || primaryServerUrl || '').trim();
    if (!primaryRaw) {
      setError('Введите основной адрес сервера');
      return;
    }

    setError('');
    setLoading(true);
    try {
      setPrimaryServerUrl(primaryRaw);
      const next = await window.desktopApi?.setServerAddresses({ primaryServerUrl: primaryRaw, reserveServerUrl: '' });
      setActiveServerKind(next?.activeServerKind || 'unknown');
      try {
        if (window.desktopApi?.restartApp) {
          await window.desktopApi.restartApp();
          return;
        }
        window.location.reload();
        return;
      } catch {}
      setLoading(false);
    } catch (err: any) {
      const msg = String(err?.message || 'Не удалось сохранить');
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-8">
      <div className="mx-auto w-full max-w-md text-center">
        <div className="mx-auto h-16 w-16 bg-brand-600 rounded-xl flex items-center justify-center shadow-lg transform -rotate-6">
          <span className="text-3xl font-bold text-white">{orgInitial}</span>
        </div>
        <h2 className="mt-6 text-3xl font-extrabold text-gray-900 tracking-tight">{resolvedOrgName}</h2>
        <p className="mt-2 text-sm text-gray-600">Подключение к серверу</p>
      </div>

      <div className="mt-8 mx-auto w-full max-w-md">
        <div className="bg-white py-8 px-10 shadow-xl shadow-gray-200/50 rounded-xl border border-gray-100">
          <div className="flex items-center justify-center gap-2 text-brand-700 font-semibold mb-6">
            <Plug className="w-5 h-5" />
            Настройка подключения
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700">Основной адрес сервера</label>
              <div className="mt-1">
                <input
                  type="text"
                  name="primaryServerUrl"
                  autoComplete="off"
                  placeholder="https://..."
                  required
                  value={primaryServerUrl}
                  onChange={(e) => {
                    setPrimaryServerUrl(e.target.value);
                    if (error) setError('');
                  }}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 text-sm"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Например: https://chat.arhivkruf.ru</p>
            </div>

            {error && <div className="text-xs text-red-600">{error}</div>}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <span className="flex items-center gap-2">Подключиться <ArrowRight className="w-4 h-4" /></span>
                )}
              </button>
            </div>
          </form>

          <p className="mt-6 text-xs text-gray-500">Если вы ввели неверный адрес и у вас пропал интернет, клиент можно будет настроить снова.</p>
        </div>
        <p className="text-center text-xs text-gray-400 mt-4">&copy; 2024 {resolvedOrgName} Enterprise. Все права защищены.</p>
      </div>
    </div>
  );
};
